import type { Action } from './action'
import type { Urn } from './urn'

/**
 * An action or resource pattern. The wildcard rules are the same for both:
 * `admin:zone:*` and `atn:tenant_t_a:*`.
 *
 * {@link Pattern.matches} and {@link Pattern.covers} are **not
 * interchangeable**: `admin:zone:read` matches `admin:zone:*`, but `admin:*`
 * is not covered by `admin:zone:*`.
 */
export type Pattern = Action.Pattern | Urn.Pattern
export namespace Pattern {
  /** Alone, names everything */
  export const WILDCARD = '*'
  /** Between the segments of an action or URN */
  export const SEPARATOR = ':'
  /** Ending a pattern, names its base and everything beneath it */
  export const TRAILING_WILDCARD = `${SEPARATOR}${WILDCARD}` as const

  /**
   * The pattern with a trailing `*` removed, and the `:` before it.
   *
   * @example
   * base('admin:zone:*')     // 'admin:zone'
   * base('*')                // ''
   * base('atn:tenant_t_a:*') // 'atn:tenant_t_a'
   * base('atn:tenant_t_a')   // 'atn:tenant_t_a' — a literal is its own base
   */
  export function base(pattern: Pattern): string {
    if (pattern === WILDCARD) {
      return ''
    }
    return pattern.endsWith(TRAILING_WILDCARD)
      ? pattern.slice(0, -TRAILING_WILDCARD.length)
      : pattern
  }

  /**
   * Whether a concrete action or resource satisfies the pattern. `X:*`
   * matches `X` itself; `atn:tenant_t_a:*` does not match `atn:tenant_t_a2`.
   * A malformed pattern, or a concrete value containing `*`, matches nothing
   */
  export function matches(pattern: Pattern, concrete: string): boolean {
    if (!isWellFormed(pattern) || concrete.includes(WILDCARD)) {
      return false
    }
    return isWithin(pattern, concrete)
  }

  /**
   * Whether every value `inner` could name is also named by `outer` —
   * containment between two patterns, not matching. Anything unparseable is
   * not covered.
   */
  export function covers(outer: Pattern, inner: Pattern): boolean {
    if (!isWellFormed(outer) || !isWellFormed(inner)) {
      return false
    }
    return isWithin(outer, inner)
  }

  /**
   * The shared rule: a literal names only itself; otherwise the base itself
   * or anything below it. The `+ ':'` keeps `atn:tenant_t_a:*` from reaching
   * `atn:tenant_t_a2`.
   *
   * Correct for `covers` only because a wildcard `value` is at least as deep
   * as its own base, so it lies within whatever its base lies within
   */
  function isWithin(pattern: Pattern, value: string): boolean {
    if (!isWildcard(pattern)) {
      return value === pattern
    }
    const patternBase = base(pattern)
    return (
      patternBase === '' ||
      value === patternBase ||
      value.startsWith(`${patternBase}${SEPARATOR}`)
    )
  }

  function isWildcard(pattern: Pattern): boolean {
    return pattern === WILDCARD || pattern.endsWith(TRAILING_WILDCARD)
  }

  /**
   * `*` only as the whole pattern or a trailing `:*` — never mid-segment.
   * A bare `:*` would have an empty base and so reach everything
   */
  function isWellFormed(pattern: Pattern): boolean {
    if (pattern === WILDCARD) {
      return true
    }
    const patternBase = base(pattern)
    return patternBase !== '' && !patternBase.includes(WILDCARD)
  }
}
